import React, { useState, useRef, useCallback } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'

import { gsap } from 'gsap'
import useAnimateInView from 'hooks/useAnimateInView'
import useTimeline from 'hooks/useTimeline'
import useOnResize from 'hooks/useOnResize'
import useDraggable from 'hooks/useDraggable'

import viewport from 'services/viewport'

import classnames from 'classnames/bind'
import useRefs from 'src/hooks/useRefs'
import css from './styles.module.scss'

const cx = classnames.bind(css)

const Slider = ({
  className,
  mobileFullWidth,
  items,
  children,
  stepsClass,
  offsetArrayLimit,
}) => {
  const [isDragging, setDragging] = useState(false)

  const slider = useRef()
  const wrapper = useRef()
  const controller = useRef()
  const cards = useRef([])
  const offsetArray = useRef([])
  const sliderMaxX = useRef(0)
  const controllerWidth = useRef(0)
  const currentIndex = useRef(0)
  const stepRefs = useRefs(offsetArrayLimit || items?.length)

  cards.current = []

  const registerRef = useCallback((node) => {
    if (!node) return
    cards.current = cards.current.concat(node)
  }, [])

  const onSliderUpdate = () => {
    const sd = sliderDraggable.current[0]
    const snap = sd.vars.snap.reduce((target, current) => {
      return Math.abs(current - sd.x) < Math.abs(target - sd.x)
        ? current
        : target
    })

    const index = sd.vars.snap.findIndex(
      (val) => Math.round(val) === Math.round(snap)
    )

    if (index !== -1 && index !== currentIndex.current) {
      updateStep(index)
    }
  }

  const handleStepClick = (id) => {
    const sd = sliderDraggable.current[0]
    updateStep(id)
    gsap.to(sd.target, {
      x: offsetArray.current[id],
      force3D: true,
    })
  }

  const updateStep = (index) => {
    gsap.to(stepRefs.current[currentIndex.current].current, {
      backgroundColor: css.backgroundColor,
    })
    currentIndex.current = index
    gsap.to(stepRefs.current[index].current, {
      backgroundColor: css.activeBackgroundColor,
    })
  }

  const sliderDraggable = useDraggable(wrapper, {
    type: 'x',
    inertia: true,
    throwResistance: 2000,
    maxDuration: 0.5,
    overshootTolerance: 0.8,
    edgeResistance: 0.9,
    zIndexBoost: false,
    onPress: () => setDragging(true),
    onRelease: () => setDragging(false),
    snap: offsetArray.current,
    onDrag: onSliderUpdate,
    onThrowUpdate: onSliderUpdate,
  })

  const setSliderSize = () => {
    if (!wrapper.current || !slider.current) return

    const sliderItem = wrapper.current.firstChild
    const style = sliderItem.currentStyle || window.getComputedStyle(sliderItem)
    const itemMargins = 2 * parseInt(style.marginRight, 10)

    if (mobileFullWidth) {
      const computedItemWidth = viewport.isMobile
        ? slider.current.getBoundingClientRect().width - itemMargins
        : null
      gsap.set(cards.current, {
        width: computedItemWidth,
      })
    }

    const { height } = wrapper.current.getBoundingClientRect()

    const itemWidth = sliderItem.getBoundingClientRect().width

    const wrapperWidth = items.length * (itemWidth + itemMargins)
    controllerWidth.current = controller.current.getBoundingClientRect().width

    gsap.set(slider.current, { height })
    gsap.set(wrapper.current, {
      position: 'absolute',
      width: wrapperWidth,
    })

    sliderMaxX.current = wrapperWidth - itemWidth - itemMargins
    offsetArray.current = items.map((_, id) => -id * (itemWidth + itemMargins))

    if (offsetArrayLimit) {
      offsetArray.current = offsetArray.current.slice(0, offsetArrayLimit)
    }

    if (sliderDraggable.current) {
      sliderDraggable.current[0].vars.snap = offsetArray.current
    }
  }

  const showTl = useTimeline({ paused: true, delay: 0.5 }, (tl) => {
    gsap.set(slider.current, {
      opacity: 1,
    })

    gsap.set(stepRefs.current[currentIndex.current].current, {
      backgroundColor: css.activeBackgroundColor,
    })

    const cardAppearDuration = 1
    const cardAppearStagger = cardAppearDuration * 0.083

    tl.fromTo(
      cards.current,
      {
        x: 800,
        opacity: 0,
      },
      {
        x: 0,
        force3D: true,
        opacity: 1,
        duration: cardAppearDuration,
        stagger: cardAppearStagger,
        ease: 'elastic.out(0.3, 0.4)',
      }
    )
  })

  const [inViewRef] = useAnimateInView({
    triggerOnce: true,
    threshold: 0.2,
    animateIn: () => showTl.restart(true),
  })

  useIsomorphicLayoutEffect(() => {
    setSliderSize()
  }, [])

  const onResize = useCallback(setSliderSize, [])
  useOnResize(onResize)

  return (
    <div className={cx(css.Slider, className)} ref={slider}>
      <div className={css.itemsContainer} ref={inViewRef}>
        <div ref={controller}>
          <div ref={wrapper} className={css.itemsWrapper}>
            {items.map((k, v) => children(k, v, registerRef))}
          </div>
        </div>
        <div className={cx(css.steps, stepsClass)}>
          {stepRefs.current.map((item, index) => (
            <div
              key={`step-${index}`}
              ref={stepRefs.current[index]}
              onClick={() => handleStepClick(index)}
              className={css.step}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Slider
